/**
 * [T270-IMPL-PREFLIGHT] Trends Adapter — T270-PY (FE-1)
 *
 * Owns the Trends tab state for the redesigned `/transactions` Insights → Trends
 * tab. Centralises three concerns the redesigned skin needs:
 *
 *   1. `selectedPeriod` — one of the 9 chip values (`auto` / `1d` / `3d` / `1w` /
 *      `2w` / `1m` / `3m` / `6m` / `1y`). Drives a chip → `(days, granularity)`
 *      lookup table mirroring the backend `transactionInsightWorkbenchPipeline`
 *      resolver rule (`≤45d → day`, `≤180d → week`, `>180d → month`).
 *   2. `chartType` — `'area' | 'bar' | 'line'`. Pure-visual swap; no fetch.
 *   3. `enabledSeries` — start at `['income', 'expenses', 'net']`. Per the
 *      binding contract §5.3 the workbench endpoint returns a single `value`
 *      series at T270-PY; the income/expenses/net three series survive via the
 *      local `buildSpendingOverTime` pipeline (NOT routed through the workbench
 *      endpoint — that is T271-PY scope). Multi-series toggles render with a
 *      "Coming in T271-PY" placeholder copy until the workbench extends.
 *
 * The adapter ALSO consumes `useTransactionInsightWorkbench` to satisfy the
 * chip→workbench round-trip, but the skin's chart data is sourced from the
 * local pipeline so it always reflects the latest `transactions` snapshot even
 * while the workbench request is in flight.
 *
 * Mount contract: this file replaces `TrendsConfigurableSkin` at the existing
 * `case 'trends'` block in `TransactionInsightsCard.tsx` (T270 §6.2.9). It
 * re-emits the wrapper the host already provides so it can be slotted into the
 * adapter site with zero host-side edit (the T270-PY PM lock forbids touching
 * `TransactionInsightsCard.tsx`).
 *
 * @see /tmp/opencode/T270-IMPL-PREFLIGHT-trends-contract.md
 * @see docs/plans/transaction-insights-trends-redesign.md §2.4 / §5 / §6.2.4
 * @see frontend/src/lib/analytics.ts:1125 (buildSpendingOverTime)
 *
 * @module transactions/TrendsAdapter
 */
import { useCallback, useMemo, useState } from 'react';

import type { Transaction, TrendGranularity } from '@/lib/types';
import { buildSpendingOverTime } from '@/lib/analytics';
import type {
  SpendingTrendPayload,
  SpendingTrendPoint,
} from '@/lib/insightsContracts';
import type { TransactionInsightWorkbenchConfig } from '@/lib/resolverContracts';
import { useTransactionInsightWorkbench } from '@/hooks/useTransactionInsightWorkbench';

import { TrendsRedesignSkin } from './TrendsRedesignSkin';

// ── Public types ────────────────────────────────────────────────────────────

/** Public chip value union. Mirrors `TrendGranularity` to keep the adapter's
 *  surface type-aligned with the existing `lib/types.ts` export. The 9th
 *  value (`'1y'`) is the only addition over the chip-set used by the legacy
 *  `GranularitySelector` (which already supports `'6m' | '1y'` in
 *  `TrendGranularity`). */
export type TrendPeriod = TrendGranularity;

/** A single row in {@link CHIP_TABLE}. */
export interface ChipMapping {
  period: TrendPeriod;
  /** Visible label (e.g. `'1D'`, `'6M'`, `'Auto'`). */
  label: string;
  /** Days passed to `buildSpendingOverTime` (or null when chip === `'auto'`). */
  days: number | null;
  /** Trend-granularity value passed to `buildSpendingOverTime` (chip-level
   *  literal — analytics.ts knows all 9). */
  forcedGranularity: TrendGranularity;
  /** Backend workbench granularity literal (`'day' | 'week' | 'month' | 'auto'`).
   *  Source: `backend/src/services/resolvers/transactionInsightWorkbenchPipeline.ts:47`
   *  rule (`≤45d → day`, `≤180d → week`, `>180d → month`). The 180-day
   *  boundary counts as `'week'` per the resolver implementation. */
  backendGranularity: 'day' | 'week' | 'month' | 'auto';
}

/** The 9 deterministic chip mappings. Source: T270-PY plan §2.4 (verbatim).
 *  No additions / deletions permitted — this table is the contract. */
export const CHIP_TABLE: readonly ChipMapping[] = [
  { period: 'auto', label: 'Auto', days: null, forcedGranularity: 'auto', backendGranularity: 'auto' },
  { period: '1d',   label: '1D',   days: 1,    forcedGranularity: '1d',   backendGranularity: 'day'   },
  { period: '3d',   label: '3D',   days: 3,    forcedGranularity: '3d',   backendGranularity: 'day'   },
  { period: '1w',   label: '1W',   days: 7,    forcedGranularity: '1w',   backendGranularity: 'day'   },
  { period: '2w',   label: '2W',   days: 14,   forcedGranularity: '2w',   backendGranularity: 'day'   },
  { period: '1m',   label: '1M',   days: 30,   forcedGranularity: '1m',   backendGranularity: 'day'   },
  { period: '3m',   label: '3M',   days: 90,   forcedGranularity: '3m',   backendGranularity: 'week'  },
  { period: '6m',   label: '6M',   days: 180,  forcedGranularity: '6m',   backendGranularity: 'week'  },
  { period: '1y',   label: '1Y',   days: 365,  forcedGranularity: '1y',   backendGranularity: 'month' },
] as const;

// ── Internal helpers ────────────────────────────────────────────────────────

type ChartType = 'area' | 'bar' | 'line';
type SeriesId = 'income' | 'expenses' | 'net';

/** Global filter-bar period → days. Mirrors `TransactionInsightsCard.tsx:792`
 *  dayMap (T253-PY). When the global filter is `'all'` / `'custom'` / unset
 *  the adapter falls back to `null` and `buildSpendingOverTime` uses an
 *  unconstrained window. */
const FILTER_PERIOD_TO_DAYS: Readonly<Record<string, number>> = {
  '7d': 7,
  '30d': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365,
};

/** Type-narrowed helper — accepts the loose filter-period union used by
 *  `UnifiedFilterBar` and normalises it to a concrete number-of-days value
 *  the analytics pipeline understands. */
function filterPeriodToDays(
  filterPeriod: string | undefined | null,
): number | null {
  if (!filterPeriod || filterPeriod === 'all' || filterPeriod === 'custom') {
    return null;
  }
  return FILTER_PERIOD_TO_DAYS[filterPeriod] ?? null;
}

function findChip(period: TrendPeriod): ChipMapping {
  return CHIP_TABLE.find((c) => c.period === period) ?? CHIP_TABLE[0];
}

// ── Props ───────────────────────────────────────────────────────────────────

export interface TrendsAdapterProps {
  /** Source transactions. The adapter derives the 3-series payload client-side
   *  via `buildSpendingOverTime` so the local pipeline survives regardless of
   *  whether the workbench request is in flight. */
  transactions: readonly Transaction[];
  /** Global filter-bar period (e.g. `'7d' | '30d' | '3m' | '6m' | '1y'`).
   *  Required for the Auto chip fallback (AC3 / binding contract §5.3). */
  filterPeriod?: string | undefined | null;
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * T270-PY Trends tab host. Owns `selectedPeriod`, `chartType`, `enabledSeries`
 * state; passes everything to the downstream `<TrendsRedesignSkin>`.
 *
 * Does NOT wrap its own host testid — `TransactionInsightsCard.tsx:949`
 * already emits `data-testid="insights-trends-tab"`, so adding another wrapper
 * here would produce a duplicate selector match. The adapter therefore renders
 * `<TrendsRedesignSkin />` directly; the host retains full ownership of the
 * `insights-trends-tab` testid (see `[T270-PY-REV2]` block below).
 */
export function TrendsAdapter({ transactions, filterPeriod }: TrendsAdapterProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TrendPeriod>('auto');
  const [chartType, setChartType] = useState<ChartType>('area');
  const [enabledSeries, setEnabledSeries] = useState<readonly SeriesId[]>([
    'income',
    'expenses',
    'net',
  ]);

  // ── Workbench config (chip → backend `(days, granularity)`) ───────────
  // Source: T270 plan §2.4 + binding contract §2.1. For `'auto'` we DO NOT
  // pass `days` (so the resolver uses its default 90-day window per
  // `transactionInsightWorkbenchPipeline.resolveWindowDays`) and let the
  // backend's `resolveAutoGranularity(windowDays)` decide DAY / WEEK / MONTH.
  // For specific chips we pass BOTH `days` and `granularity` so the chart
  // renders at the matching bucket size.
  const workbenchConfig = useMemo<TransactionInsightWorkbenchConfig>(() => {
    const chip = findChip(selectedPeriod);
    const cfg: TransactionInsightWorkbenchConfig = {
      kind: 'transaction-insight-workbench',
      dimension: 'category',
      measure: 'netSpend',
      comparison: 'none',
      topN: 6,
      granularity: chip.backendGranularity,
    };
    if (chip.days !== null) {
      cfg.days = chip.days;
    }
    return cfg;
  }, [selectedPeriod]);

  const workbench = useTransactionInsightWorkbench(workbenchConfig);

  // ── Local 3-series payload (binding contract §5.1) ────────────────────
  // Income / Expenses / Net survive because we run the EXISTING
  // `buildSpendingOverTime(transactions, currentPeriodDays, currentGranularity)`
  // locally — NOT the workbench route (T271-PY scope). Only `buildSpendingOverTime`
  // returns `{ date, income, expense, net }`; the workbench route returns a
  // single `value` series. This block does the field-name swap
  // (`date` → `month`, `expense` → `expenses`) and the resulting
  // `SpendingTrendPayload` is what the skin renders.
  const trendsMonths = useMemo<SpendingTrendPayload>(() => {
    const chip = findChip(selectedPeriod);
    const currentPeriodDays =
      selectedPeriod === 'auto'
        ? filterPeriodToDays(filterPeriod)
        : chip.days;
    const currentGranularity =
      selectedPeriod === 'auto' ? 'auto' : chip.forcedGranularity;
    return {
      months: buildSpendingOverTime(
        transactions as Transaction[],
        currentPeriodDays,
        currentGranularity,
      ).map(
        (p): SpendingTrendPoint => ({
          month: p.date,
          income: p.income,
          expenses: p.expense,
          net: p.net,
        }),
      ),
    };
  }, [transactions, selectedPeriod, filterPeriod]);

  // ── Auto chip suffix (binding contract §2.2 + AC3) ────────────────────
  // The chip's Auto suffix echoes the workbench's resolved granularity
  // (`DAY` / `WEEK` / `MONTH`). When the workbench hasn't returned yet we
  // fall back to `'DAY'` (≤45d) so the suffix is always deterministic.
  const resolvedGranularity = workbench.data?.meta?.granularity ?? 'day';

  // ── State-change callbacks (stable references for the skin memo) ──────
  const handlePeriodChange = useCallback(
    (p: TrendPeriod) => setSelectedPeriod(p),
    [],
  );
  const handleChartTypeChange = useCallback(
    (t: ChartType) => setChartType(t),
    [],
  );
  const handleSeriesToggle = useCallback((s: SeriesId) => {
    setEnabledSeries((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    );
  }, []);

  // Adapter-view loading + error: only block the local pipeline when the
  // workbench hasn't completed at least once. We DO show the local payload
  // (computed from `transactions`) the moment transactions change, so the
  // user always sees up-to-date data even mid-fetch.
  const loading = workbench.loading && !workbench.data;

  // [T270-PY-REV2] Removed the duplicate `data-testid="insights-trends-tab"` wrapper.
  // The host mount (`TransactionInsightsCard.tsx`) already wraps `<TrendsAdapter />`
  // with that exact testid; rendering a second copy here produced a duplicate
  // selector match and broke E2E root scoping. Child component + host behaviour preserved.
  return (
    <TrendsRedesignSkin
      selectedPeriod={selectedPeriod}
      chartType={chartType}
      enabledSeries={enabledSeries}
      data={trendsMonths}
      loading={loading}
      error={
        workbench.error
          ? {
              code: 'WORKBENCH_FETCH_FAILED',
              message: workbench.error,
            }
          : undefined
      }
      resolvedGranularity={resolvedGranularity}
      onPeriodChange={handlePeriodChange}
      onChartTypeChange={handleChartTypeChange}
      onToggleSeries={handleSeriesToggle}
    />
  );
}

export default TrendsAdapter;
